// ./src/components/generic/FontFamilySelector/FontFamilySelector.tsx
import React from "react";
import { Select, SelectProps } from "@mantine/core";

const FONT_FAMILY_OPTIONS = [
  {
    value: "inherit",
    label: "Inherit",
    description: "",
  },
  {
    value: "Inter, system-ui, -apple-system, sans-serif",
    label: "Inter",
    description: "Modern, clean sans-serif",
  },
  {
    value: "Roboto, sans-serif",
    label: "Roboto",
    description: "Google's versatile sans-serif",
  },
  {
    value: "Open Sans, sans-serif",
    label: "Open Sans",
    description: "Friendly, readable sans-serif",
  },
  {
    value: "Lato, sans-serif",
    label: "Lato",
    description: "Professional sans-serif",
  },
  {
    value: "Montserrat, sans-serif",
    label: "Montserrat",
    description: "Geometric sans-serif",
  },
  {
    value: "Poppins, sans-serif",
    label: "Poppins",
    description: "Geometric, modern sans-serif",
  },
  {
    value: "Source Sans Pro, sans-serif",
    label: "Source Sans Pro",
    description: "Adobe's elegant sans-serif",
  },
  {
    value: "Raleway, sans-serif",
    label: "Raleway",
    description: "Elegant, thin sans-serif",
  },
  {
    value: "Ubuntu, sans-serif",
    label: "Ubuntu",
    description: "Humanist sans-serif",
  },
  {
    value: "Nunito, sans-serif",
    label: "Nunito",
    description: "Rounded, friendly sans-serif",
  },
  {
    value: "Georgia, serif",
    label: "Georgia",
    description: "Classic serif font",
  },
  {
    value: "Times New Roman, serif",
    label: "Times New Roman",
    description: "Traditional serif font",
  },
  {
    value: "Courier New, monospace",
    label: "Courier New",
    description: "Monospace font",
  },
  {
    value: "Arial, sans-serif",
    label: "Arial",
    description: "Universal sans-serif",
  },
  {
    value: "Verdana, sans-serif",
    label: "Verdana",
    description: "Web-safe sans-serif",
  },
] as const;

export interface FontFamilySelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Current font family value */
  value: string | null | undefined;

  /** Callback when font family changes */
  onChange: (value: string | null) => void;

  /** Whether to show descriptions in the dropdown */
  withDescriptions?: boolean;

  /** Allow custom font family input */
  allowCustom?: boolean;
}

export const FontFamilySelector: React.FC<FontFamilySelectorProps> = ({
  value,
  onChange,
  withDescriptions = false,
  allowCustom = false,
  placeholder = "Select font family",
  label,
  description,
  error,
  ...selectProps
}) => {
  // Prepare select data with optional descriptions
  let selectData: any[] = FONT_FAMILY_OPTIONS.map((option) => {
    let label: string = option.label;

    if (withDescriptions) {
      label = `${option.label} - ${option.description}`;
    }

    return {
      value: option.value,
      label,
    };
  });

  // If allowCustom and value is not in presets, add it to the data
  if (allowCustom && value) {
    const isPreset = FONT_FAMILY_OPTIONS.some((opt) => opt.value === value);
    if (!isPreset) {
      selectData = [
        ...selectData,
        {
          value,
          label: `Custom: ${value}`,
        },
      ];
    }
  }

  return (
    <Select
      {...selectProps}
      label={label}
      description={description}
      error={error}
      data={selectData}
      value={value || null}
      onChange={onChange}
      placeholder={placeholder}
      searchable
      allowDeselect={false}
      clearable={false}
    />
  );
};

// Export font family options for use in other components
export { FONT_FAMILY_OPTIONS };
